import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	redirect,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { Calendar, CalendarCheck01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import z from "zod";
import { Button } from "@/components/base/buttons/button";
import { DeleteJobTypeModal } from "@/components/Organisms/DeleteJobTypeModal/DeleteJobTypeModal";
import type { DrawerHeaderMetaItem } from "@/components/Organisms/DrawerHeader/DrawerHeader";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import type { EntityTypeDetailsPatch } from "@/features/entity-types/components/entity-type-details-panel";
import { EntityTypeDrawer } from "@/features/entity-types/components/entity-type-drawer";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { useSyncedLocationRequired } from "@/features/entity-types/hooks/use-synced-location-required";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import { authQueryOptions } from "@/services/auth/auth.options";
import {
	useDisableJobType,
	useDisableJobTypeSubOrganization,
	useDuplicateJobType,
	useEnableJobType,
	useEnableJobTypeSubOrganization,
	useUpdateJobType,
} from "@/services/job/job.mutations";
import { jobQueryOptions } from "@/services/job/job.options";
import { organizationQueryOptions } from "@/services/organization/organization.options";
import {
	canShowAccessTab,
	canShowActivityTab,
	getJobTypeDisplayName,
} from "@/utils/job-type-detail/job-type-detail.utils";
import {
	JobTypeDrawerContext,
	type JobTypeDrawerContextValue,
} from "@/utils/job-type-detail/job-type-drawer-context";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";
import { showSuccessToast } from "@/utils/toast.utils";

const SearchSchema = z.object({
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z
		.enum(["drawer", "modal", "full"])
		.optional()
		.default("drawer"),
});

type JobTypeTab =
	| "details"
	| "access"
	| "schema"
	| "lifecycle"
	| "rules"
	| "activity";

function getCurrentTab(pathname: string): JobTypeTab {
	const segment = pathname.split("/").pop();
	if (
		segment === "details" ||
		segment === "access" ||
		segment === "schema" ||
		segment === "lifecycle" ||
		segment === "rules" ||
		segment === "activity"
	) {
		return segment;
	}
	return "details";
}

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId",
)({
	component: OrgJobTypeIdLayout,
	params: {
		parse: (raw) => {
			const p = raw as { orgId: string; jobTypeId: string };
			return {
				orgId: z.string().parse(p.orgId),
				jobTypeId: z.string().parse(p.jobTypeId),
			};
		},
	},
	validateSearch: (search) => SearchSchema.parse(search),
	beforeLoad: ({ location, params, search }) => {
		const normalized = location.pathname.replace(/\/$/, "");
		const exactRoot = normalized.endsWith(
			`/configuration/jobTypes/${params.jobTypeId}`,
		);
		if (exactRoot) {
			const parsed = SearchSchema.safeParse(search);
			const s = parsed.success ? parsed.data : SearchSchema.parse({});
			throw redirect({
				to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/details",
				params: { orgId: params.orgId, jobTypeId: params.jobTypeId },
				search: {
					mode: s.mode,
					presentation: s.presentation,
				},
			});
		}
	},
});

function OrgJobTypeIdLayout() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isFormRoute = useRouterState({
		select: (s) =>
			s.matches.some(
				(m) =>
					typeof m.routeId === "string" &&
					m.routeId.endsWith("/$jobTypeId/form"),
			),
	});
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/jobTypes/$jobTypeId",
	});

	const isFullRoute = /\/configuration\/jobTypes\/[^/]+\/full/.test(pathname);

	/** Always scope API calls to the org from the URL so detail payloads include `defaultType` / source correctly for custom types. */
	const organizationIdForApi = params.orgId;
	const [isOpen, setIsOpen] = useState(() => !isFullRoute);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const detailQuery = useQuery(
		jobQueryOptions.jobTypeDetail(params.jobTypeId, {
			organizationId: organizationIdForApi,
		}),
	);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteJobType",
			entityType: "JobType",
			entityId: params.jobTypeId,
		}),
	});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditJobType",
			entityType: "JobType",
			entityId: params.jobTypeId,
		}),
	});
	const accessEdit = useTypeAccessEditAllowed("JobType", params.jobTypeId);
	const subOrgCountQuery = useQuery(
		organizationQueryOptions.subOrganizations.count(organizationIdForApi, {}),
	);
	const hasSubOrganizations = (subOrgCountQuery.data ?? 0) > 0;

	const updateMutation = useUpdateJobType();
	const duplicateMutation = useDuplicateJobType();
	const { duplicate: duplicateJobType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (jobTypeId) => ({
				jobTypeId,
				organizationId: organizationIdForApi,
			}),
			onDuplicated: (jobTypeId) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/jobTypes/$jobTypeId/details",
					params: { orgId: organizationIdForApi, jobTypeId },
					search: { mode: "view", presentation: "full" },
				});
			},
			errorMessage: t("admin.jobTypes.modal.createError"),
		});
	const enableMutation = useEnableJobType();
	const disableMutation = useDisableJobType();
	const enableSubOrgMutation = useEnableJobTypeSubOrganization();
	const disableSubOrgMutation = useDisableJobTypeSubOrganization();

	const jobType = detailQuery.data ?? null;
	const [locationRequired, setLocationRequired] = useSyncedLocationRequired(
		jobType?.id,
		jobType?.locationRequired,
	);

	const [editedName, setEditedName] = useState("");
	const [editedDescription, setEditedDescription] = useState("");
	const [editedIcon, setEditedIcon] = useState("");
	const [draftName, setDraftName] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [draftIcon, setDraftIcon] = useState("");
	// null = org-wide access (no sub-orgs); [] = none selected; string[] = grant list
	const [draftSubOrgIds, setDraftSubOrgIds] = useState<string[] | null>(null);

	useEffect(() => {
		if (!jobType?.id) return;

		const isAccessEdit =
			(search.mode ?? "view") === "edit" &&
			getCurrentTab(pathname) === "access";
		const isDetailsEdit = (search.mode ?? "view") === "edit";
		const name = getJobTypeDisplayName(jobType);
		const description = jobType.description ?? "";
		const icon = jobType.icon ?? "";
		if (!isDetailsEdit || draftName === editedName) {
			setEditedName(name);
			setDraftName(name);
		}
		if (!isDetailsEdit || draftDescription === editedDescription) {
			setEditedDescription(description);
			setDraftDescription(description);
		}
		if (!isDetailsEdit || draftIcon === editedIcon) {
			setEditedIcon(icon);
			setDraftIcon(icon);
		}
		if (isAccessEdit) return;
		const subOrgIds = (jobType.jobTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		setDraftSubOrgIds(
			subOrgIds.length > 0 ? subOrgIds : jobType.enabled === false ? [] : null,
		);
	}, [jobType, pathname, search.mode]);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(() => {
			void navigate({
				to: "/org-admin/$orgId/configuration/jobTypes",
				params: { orgId: params.orgId },
				replace: true,
			});
		}, 200);
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (!open) handleClose();
	};

	if (isFormRoute) {
		return (
			<div className="flex min-h-0 flex-1 flex-col bg-background-primary">
				<Outlet />
			</div>
		);
	}

	const isDetailLoading = detailQuery.isLoading;
	const isDetailError =
		!isDetailLoading && (detailQuery.isError || jobType === null);
	const isDetailReady =
		!isDetailLoading && !detailQuery.isError && jobType !== null;

	const loadedJobType = jobType ?? undefined;
	const displayJobType =
		loadedJobType && isDetailReady
			? {
					...loadedJobType,
					name: editedName,
					description: editedDescription,
					icon: editedIcon || loadedJobType.icon,
				}
			: null;

	const canEdit = !!loadedJobType && isDetailReady && !!canEditQuery.data;
	const showActivity =
		!!loadedJobType &&
		isDetailReady &&
		canShowActivityTab("organization", loadedJobType);
	const showAccess = canShowAccessTab("organization");
	const currentTab = getCurrentTab(pathname);
	const isEditing =
		search.mode === "edit" &&
		(currentTab === "access" ? accessEdit.allowed : canEdit);

	useEffect(() => {
		if (isFormRoute || isFullRoute) return;
		if ((search.mode ?? "view") !== "edit") return;
		if (currentTab === "access") {
			if (accessEdit.isPending || accessEdit.allowed) return;
		} else if (!isDetailReady || canEditQuery.isPending || canEdit) {
			return;
		}
		void navigate({
			to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/${currentTab}`,
			search: { mode: "view", presentation: search.presentation },
		});
	}, [
		accessEdit.allowed,
		accessEdit.isPending,
		canEdit,
		canEditQuery.isPending,
		currentTab,
		isDetailReady,
		isFormRoute,
		isFullRoute,
		navigate,
		params.jobTypeId,
		params.orgId,
		search.mode,
		search.presentation,
	]);

	const isValid = draftName.trim().length > 0;

	const tabs: { id: JobTypeTab; label: string }[] =
		loadedJobType && isDetailReady
			? [
					{ id: "details", label: t("admin.jobTypes.drawer.tabs.details") },
					...(showAccess
						? [
								{
									id: "access" as const,
									label: t("admin.jobTypes.drawer.tabs.access"),
								},
							]
						: []),
					{ id: "schema", label: t("admin.jobTypes.drawer.tabs.form") },
					{
						id: "lifecycle",
						label: t("admin.jobTypes.drawer.tabs.lifecycle"),
					},
					{ id: "rules", label: t("admin.jobTypes.drawer.tabs.rules") },
					...(showActivity
						? [
								{
									id: "activity" as const,
									label: t("admin.jobTypes.drawer.tabs.activity"),
								},
							]
						: []),
				]
			: [];

	const currentTabInfo = tabs.find((tab) => tab.id === currentTab);
	const editModeTabs = currentTabInfo
		? [{ id: currentTabInfo.id, label: `Edit ${currentTabInfo.label}` }]
		: [];

	const canDelete =
		!!loadedJobType && isDetailReady && !!canDeleteQuery.data && !isEditing;
	const canDuplicate = isDetailReady && !isEditing;

	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending ||
		enableSubOrgMutation.isPending ||
		disableSubOrgMutation.isPending;

	const saveDraftFn = async (): Promise<void> => {
		if (!loadedJobType || !isValid || isSavingAny) return;
		if (currentTab === "access" ? !accessEdit.allowed : !canEdit) return;
		try {
			if (currentTab === "access") {
				if (hasSubOrganizations) {
					const serverIds = (loadedJobType.jobTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					const nextIds = draftSubOrgIds ?? [];
					const toEnable = nextIds.filter((id) => !serverIds.includes(id));
					const toDisable = serverIds.filter((id) => !nextIds.includes(id));
					await Promise.all([
						...toEnable.map((subOrgId) =>
							enableSubOrgMutation.mutateAsync({
								typeId: loadedJobType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
						...toDisable.map((subOrgId) =>
							disableSubOrgMutation.mutateAsync({
								typeId: loadedJobType.id,
								subOrgId,
								organizationId: organizationIdForApi,
							}),
						),
					]);
				} else {
					if (draftSubOrgIds === null) {
						await enableMutation.mutateAsync({
							jobTypeId: loadedJobType.id,
							organizationId: organizationIdForApi,
						});
					} else {
						await disableMutation.mutateAsync({
							jobTypeId: loadedJobType.id,
							organizationId: organizationIdForApi,
						});
					}
				}
				showSuccessToast({
					message: t("admin.jobTypes.drawer.access.toast.saveSuccess"),
				});
			} else {
				const currentSubOrgIds = (loadedJobType.jobTypeSubOrganizations ?? [])
					.map((s) => s.id)
					.filter((id): id is string => id !== null);
				await updateMutation.mutateAsync({
					jobTypeId: loadedJobType.id,
					data: {
						name: draftName.trim(),
						description: draftDescription || null,
						icon: draftIcon || null,
						typeId: loadedJobType.id,
						statusLifecycleId: loadedJobType.lifecycleId ?? null,
						subOrganizationIds:
							currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
						locationRequired,
					},
					organizationId: organizationIdForApi,
				});
				setEditedName(draftName.trim());
				setEditedDescription(draftDescription);
				setEditedIcon(draftIcon);
				showSuccessToast({
					message: t("admin.jobTypes.drawer.details.toast.saveSuccess"),
				});
			}
			void navigate({
				to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/${currentTab}`,
				search: { mode: "view", presentation: search.presentation },
			});
		} catch {
			// Global mutation error handler in root-provider.tsx already shows the error toast
		}
	};

	const persistDetails = async (
		patch: EntityTypeDetailsPatch,
	): Promise<void> => {
		if (!loadedJobType) return;
		const name = (patch.name ?? editedName).trim();
		const description =
			patch.description !== undefined
				? (patch.description ?? "")
				: editedDescription;
		const icon = patch.icon !== undefined ? (patch.icon ?? "") : editedIcon;
		const currentSubOrgIds = (loadedJobType.jobTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			jobTypeId: loadedJobType.id,
			data: {
				name,
				description: description || null,
				icon: icon || null,
				typeId: loadedJobType.id,
				statusLifecycleId: loadedJobType.lifecycleId ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired,
			},
			organizationId: organizationIdForApi,
		});
		setEditedName(name);
		setDraftName(name);
		setEditedDescription(description);
		setDraftDescription(description);
		setEditedIcon(icon);
		setDraftIcon(icon);
	};

	const persistLocationRequired = async (next: boolean): Promise<void> => {
		if (!loadedJobType) return;
		const currentSubOrgIds = (loadedJobType.jobTypeSubOrganizations ?? [])
			.map((s) => s.id)
			.filter((id): id is string => id !== null);
		await updateMutation.mutateAsync({
			jobTypeId: loadedJobType.id,
			data: {
				name: (editedName || loadedJobType.name || "").trim(),
				description: editedDescription || loadedJobType.description || null,
				icon: editedIcon || loadedJobType.icon || null,
				typeId: loadedJobType.id,
				statusLifecycleId: loadedJobType.lifecycleId ?? null,
				subOrganizationIds:
					currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
				locationRequired: next,
			},
			organizationId: organizationIdForApi,
		});
		setLocationRequired(next);
	};

	const contextValue: JobTypeDrawerContextValue | null =
		loadedJobType && isDetailReady && displayJobType
			? {
					scope: "organization",
					jobType: displayJobType,
					mode: search.mode,
					setMode: (mode) => {
						void navigate({
							to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/${currentTab}`,
							search: { mode, presentation: search.presentation },
						});
					},
					canEdit,
					draftName,
					draftDescription,
					draftIcon,
					setDraftName,
					setDraftDescription,
					setDraftIcon,
					draftSubOrgIds,
					setDraftSubOrgIds,
					isSaving: isSavingAny,
					isValid,
					isDirty:
						draftName.trim() !== editedName ||
						draftDescription !== editedDescription ||
						draftIcon !== editedIcon,
					saveDraft: saveDraftFn,
					cancelDraft: () => {
						setDraftName(editedName);
						setDraftDescription(editedDescription);
						setDraftIcon(editedIcon);
						const subOrgIds = (loadedJobType.jobTypeSubOrganizations ?? [])
							.map((s) => s.id)
							.filter((id): id is string => id !== null);
						setDraftSubOrgIds(
							subOrgIds.length > 0
								? subOrgIds
								: loadedJobType.enabled === false
									? []
									: null,
						);
						void navigate({
							to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/${currentTab}`,
							search: { mode: "view", presentation: search.presentation },
						});
					},
					persistDetails,
					locationRequired,
					persistLocationRequired,
				}
			: null;

	const headerTitle =
		displayJobType !== null
			? getJobTypeDisplayName(displayJobType)
			: t("admin.jobTypes.drawer.header.titlePlaceholder");

	const drawerMetadata: DrawerHeaderMetaItem[] = [];
	if (loadedJobType?.createdAt) {
		drawerMetadata.push({
			id: "created",
			label: createdMetadataLabel(t, loadedJobType.createdAt),
			icon: Calendar,
		});
	}
	if (loadedJobType?.updatedAt) {
		drawerMetadata.push({
			id: "updated",
			label: updatedMetadataLabel(t, loadedJobType.updatedAt),
			icon: CalendarCheck01,
		});
	}

	const editFooter =
		isEditing && currentTab !== "access" && contextValue ? (
			<div className="flex w-full items-center justify-end gap-3">
				<Button
					color="secondary"
					size="md"
					onClick={contextValue.cancelDraft}
					isDisabled={isSavingAny}
				>
					{t("admin.jobTypes.drawer.actions.cancel")}
				</Button>
				<Button
					color="primary"
					size="md"
					data-testid="job-type-drawer-save-btn"
					onClick={() => void contextValue.saveDraft()}
					isDisabled={!isValid || isSavingAny}
					isLoading={isSavingAny}
				>
					{t("admin.jobTypes.drawer.actions.save")}
				</Button>
			</div>
		) : undefined;

	if (isFullRoute) {
		return contextValue ? (
			<JobTypeDrawerContext.Provider value={contextValue}>
				<Outlet />
			</JobTypeDrawerContext.Provider>
		) : (
			<Outlet />
		);
	}

	// useRouterState updates before the component unmounts, so during navigation
	// away from any $jobTypeId route the pathname briefly shows the new URL.
	// Guard against mounting the Drawer during that window.
	const isOnJobTypeSubroute = /\/configuration\/jobTypes\/[^/]+\//.test(
		pathname,
	);

	if (!isOnJobTypeSubroute) {
		return null;
	}

	return (
		<>
			<EntityTypeDrawer
				type="job"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				title={headerTitle}
				metadata={drawerMetadata}
				workspaceLabel={t("main.jobs.workspace.action")}
				showWorkspace={isDetailReady}
				onWorkspace={() => {
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/full`,
						search: { tab: "details", mode: "view" },
					});
				}}
				duplicateLabel={
					canDuplicate ? t("admin.jobTypes.table.actions.duplicate") : undefined
				}
				onDuplicate={
					canDuplicate ? () => duplicateJobType(params.jobTypeId) : undefined
				}
				deleteLabel={
					canDelete
						? t("admin.jobTypes.drawer.footer.deleteJobType")
						: undefined
				}
				onDelete={canDelete ? () => setDeleteModalOpen(true) : undefined}
				isLoading={isDetailLoading}
				isError={isDetailError}
				errorTitle={t("admin.jobTypes.drawer.errors.loadFailed")}
				errorDescription={t("admin.jobTypes.drawer.errors.notFound")}
				tabs={isEditing ? editModeTabs : tabs}
				currentTab={currentTab}
				onTabChange={(tabId) =>
					void navigate({
						to: `/org-admin/${params.orgId}/configuration/jobTypes/${params.jobTypeId}/${tabId}`,
						search: {
							mode: search.mode,
							presentation: search.presentation,
						},
					})
				}
				footer={editFooter}
				data-testid="job-type-drawer"
			>
				{isDetailReady && contextValue ? (
					<JobTypeDrawerContext.Provider value={contextValue}>
						<Outlet />
					</JobTypeDrawerContext.Provider>
				) : null}
			</EntityTypeDrawer>

			<DeleteJobTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={handleClose}
				jobTypeId={params.jobTypeId}
				jobTypeName={loadedJobType ? getJobTypeDisplayName(loadedJobType) : ""}
				organizationId={organizationIdForApi}
				scope="organization"
			/>

			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.jobTypes.duplicate.inProgress")}
				testId="jobtype-drawer-duplicate-in-progress"
			/>
		</>
	);
}
